import { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";

import { allCountries } from "country-telephone-data";
import { FiUser, FiMail, FiPhone, FiHome, FiMapPin, FiSave, FiUpload, FiCamera } from "react-icons/fi";
import "../styles/UserProfile.css";

export default function UserProfile() {
  const { user, token, login } = useAuth();

  /* ======================
     SPLIT MOBILE NUMBER SAFELY
  ====================== */
  const initialCode = user.countryCode || "+44";
  const fullMobile = user.mobile || "";

  const initialMobile =
    fullMobile.startsWith(initialCode)
      ? fullMobile.replace(initialCode, "")
      : fullMobile;

  /* ======================
     STATE
  ====================== */
  const [name, setName] = useState(user.name || "");
  const [countryCode, setCountryCode] = useState(initialCode);
  const [mobileNumber, setMobileNumber] = useState(initialMobile);

  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(
    user.userProfile?.photo || "/avatar.png"
  );

  const [address, setAddress] = useState({
    houseNumber: user.userProfile?.address?.houseNumber || "",
    street: user.userProfile?.address?.street || "",
    landmark: user.userProfile?.address?.landmark || "",
    town: user.userProfile?.address?.town || "",
    county: user.userProfile?.address?.county || "",
    postcode: user.userProfile?.address?.postcode || "",
    country: (user.userProfile?.address?.country || "United Kingdom").replace(/\s*\(.*?\)\s*/g, "")
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  /* ======================
     COUNTRY CODE TO COUNTRY NAME MAPPING
  ====================== */
  const countryCodeToName = useMemo(() => {
    const mapping = {};
    allCountries.forEach(country => {
      // 🔥 REMOVE NATIVE LANGUAGE FROM NAME
      const cleanName = country.name.replace(/\s*\(.*?\)\s*/g, "");
      mapping[country.dialCode] = cleanName;
    });
    return mapping;
  }, []);


  // Get country name from country code
  const getCountryNameFromCode = (code) => {
    if (!code) return address.country || "United Kingdom";

    // Remove any non-digit characters except plus
    const cleanCode = code.startsWith('+') ? code : `+${code.replace(/\D/g, '')}`;

    // Try to find exact match first
    if (countryCodeToName[cleanCode]) {
      return countryCodeToName[cleanCode];
    }

    // Try to find match without plus
    const codeWithoutPlus = cleanCode.replace('+', '');
    if (countryCodeToName[codeWithoutPlus]) {
      return countryCodeToName[codeWithoutPlus];
    }

    // Default to current country or United Kingdom
    return address.country || "United Kingdom";
  };

  /* ======================
     EFFECT: UPDATE COUNTRY WHEN COUNTRY CODE CHANGES
  ====================== */
  useEffect(() => {
    if (countryCode) {
      const countryName = getCountryNameFromCode(countryCode);
      if (countryName !== address.country) {
        setAddress(prev => ({
          ...prev,
          country: countryName
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  /* ======================
     COUNTRY CODE OPTIONS
  ====================== */
  const countryOptions = useMemo(() => {
    return allCountries
      .filter(country => country.dialCode)
      .map(country => {
        // 🔥 REMOVE NATIVE LANGUAGE (anything inside brackets)
        const cleanName = country.name.replace(/\s*\(.*?\)\s*/g, "");

        return {
          label: `${cleanName} (+${country.dialCode})`,
          value: `+${country.dialCode}`,
          name: cleanName
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);


  // Find current country option
  const currentCountryOption = countryOptions.find(
    (c) => c.value === countryCode
  ) || countryOptions.find(c => c.value === "+44"); // Default to UK

  /* ======================
     SELECT STYLES (LUXURY THEME)
  ====================== */
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      boxShadow: "none",
      backgroundColor: state.isDisabled ? "#f9fafb" : "#ffffff",
      borderColor: state.isFocused ? "#bfa56a" : "#d1d5db",
      "&:hover": {
        borderColor: state.isFocused ? "#bfa56a" : "#9ca3af"
      },
      transition: "all 0.3s ease"
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      zIndex: 1000
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "rgba(191, 165, 106, 0.1)"
        : state.isFocused
          ? "rgba(191, 165, 106, 0.05)"
          : "#ffffff",
      color: state.isSelected ? "#3a2f2c" : "#1f2937",
      fontSize: "14px",
      padding: "10px 16px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "rgba(191, 165, 106, 0.1)"
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: "#3a2f2c",
      fontSize: "14px",
      fontWeight: "500"
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "14px"
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      "&:hover": {
        color: "#3a2f2c"
      }
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "#e5e7eb"
    })
  };

  /* ======================
     IMAGE HANDLER
  ====================== */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  /* ======================
     UPLOAD PROFILE PHOTO
  ====================== */
  const uploadPhoto = async () => {
    if (!photoFile) return;

    const formData = new FormData();
    formData.append("photo", photoFile);

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/user/profile/photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      login(token, res.data.user);
      setMessage({ text: "Profile photo updated successfully", type: "success" });
    } catch (err) {
      setMessage({ text: "Image upload failed", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     UPDATE PROFILE
  ====================== */
  const updateProfile = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!mobileNumber || mobileNumber.length < 6) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/auth/user/profile`,
        {
          name,
          countryCode,
          mobile: countryCode + mobileNumber.replace(/\D/g, ''), // Combine and clean
          address
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      login(token, res.data.user);
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Profile update failed"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle input change for address
  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  // Handle country code select change
  const handleCountryCodeChange = (selectedOption) => {
    setCountryCode(selectedOption.value);
  };

  return (
    <div className="luxury-profile-container">
      {/* Header */}
      <div className="luxury-profile-header">
        <div className="luxury-profile-header-content">
          <div>
            <h1 className="luxury-profile-title">My Profile</h1>
            <p className="luxury-profile-subtitle">Manage your personal information and preferences</p>
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`luxury-profile-message luxury-profile-message-${message.type}`}>
          <div className="luxury-message-content">
            {message.type === "success" ? "✓" : "!"}
            <span>{message.text}</span>
          </div>
          <button
            className="luxury-message-close"
            onClick={() => setMessage({ text: "", type: "" })}
          >
            ×
          </button>
        </div>
      )}

      <div className="luxury-profile-content">
        {/* Left Column - Profile Image & Quick Info */}
        <div className="luxury-profile-sidebar">
          {/* Profile Image Card */}
          <div className="luxury-profile-image-card">
            <div className="luxury-profile-image-wrapper">
              <img
                src={preview}
                alt="Profile"
                className="luxury-profile-avatar"
              />
              <div className="luxury-profile-image-overlay">
                <label htmlFor="profile-upload" className="luxury-profile-upload-label">
                  <FiCamera size={20} />
                  <span>Change Photo</span>
                </label>
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="luxury-profile-upload-input"
                />
              </div>
            </div>

            <div className="luxury-profile-upload-actions">
              <button
                onClick={uploadPhoto}
                disabled={loading || !photoFile}
                className="luxury-profile-upload-btn"
              >
                <FiUpload size={16} />
                <span>{loading ? "Uploading..." : "Upload Photo"}</span>
              </button>
            </div>

            <div className="luxury-profile-quick-info">
              <div className="luxury-info-item">
                <span className="luxury-info-label">Account Status</span>
                <span className="luxury-info-value luxury-status-active">
                  Active
                </span>
              </div>
              <div className="luxury-info-item">
                <span className="luxury-info-label">Member Since</span>
                <span className="luxury-info-value">
                  {new Date(user.createdAt).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short'
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Profile Form */}
        <div className="luxury-profile-main">
          {/* Personal Information Card */}
          <div className="luxury-profile-form-card">
            <div className="luxury-form-card-header">
              <div className="luxury-form-card-icon">
                <FiUser size={20} />
              </div>
              <h3 className="luxury-form-card-title">Personal Information</h3>
            </div>

            <div className="luxury-profile-form">
              <div className="luxury-form-row">
                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiUser size={14} />
                    <span>Full Name</span>
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="luxury-form-input"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMail size={14} />
                    <span>Email Address</span>
                  </label>
                  <input
                    value={user.email}
                    disabled
                    className="luxury-form-input luxury-form-input-disabled"
                  />
                </div>
              </div>

              <div className="luxury-form-row">
                {/* Country Code Select */}
                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiPhone size={14} />
                    <span>Country Code</span>
                  </label>
                  <Select
                    options={countryOptions}
                    value={currentCountryOption}
                    onChange={handleCountryCodeChange}
                    styles={selectStyles}
                    placeholder="Select country code"
                    className="country-select"
                    classNamePrefix="luxury-select"
                    isSearchable
                    menuPlacement="auto"
                  />
                </div>

                {/* Mobile Number Input */}
                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiPhone size={14} />
                    <span>Mobile Number</span>
                  </label>
                  <input
                    value={mobileNumber}
                    onChange={(e) => {
                      // Allow only numbers
                      const value = e.target.value.replace(/\D/g, '');
                      setMobileNumber(value);
                    }}
                    className="luxury-form-input"
                    placeholder="Enter mobile number"
                    type="tel"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information Card */}
          <div className="luxury-profile-form-card">
            <div className="luxury-form-card-header">
              <div className="luxury-form-card-icon">
                <FiHome size={20} />
              </div>
              <h3 className="luxury-form-card-title">Address Information</h3>
            </div>

            <div className="luxury-profile-form">
              <div className="luxury-form-row">
                <div className="luxury-form-group luxury-form-group-small">
                  <label className="luxury-form-label">
                    <FiHome size={14} />
                    <span>House / Flat No</span>
                  </label>
                  <input
                    value={address.houseNumber}
                    onChange={(e) => handleAddressChange('houseNumber', e.target.value)}
                    className="luxury-form-input"
                    placeholder="123"
                  />
                </div>

                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>Street</span>
                  </label>
                  <input
                    value={address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className="luxury-form-input"
                    placeholder="Main Street"
                  />
                </div>
              </div>

              <div className="luxury-form-row">
                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>Landmark</span>
                  </label>
                  <input
                    value={address.landmark}
                    onChange={(e) => handleAddressChange('landmark', e.target.value)}
                    className="luxury-form-input"
                    placeholder="Near City Hall"
                  />
                </div>
              </div>

              <div className="luxury-form-row">
                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>Town / City</span>
                  </label>
                  <input
                    value={address.town}
                    onChange={(e) => handleAddressChange('town', e.target.value)}
                    className="luxury-form-input"
                    placeholder="London"
                  />
                </div>

                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>County</span>
                  </label>
                  <input
                    value={address.county}
                    onChange={(e) => handleAddressChange('county', e.target.value)}
                    className="luxury-form-input"
                    placeholder="Greater London"
                  />
                </div>
              </div>

              <div className="luxury-form-row">
                <div className="luxury-form-group luxury-form-group-small">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>Postcode</span>
                  </label>
                  <input
                    value={address.postcode}
                    onChange={(e) => handleAddressChange('postcode', e.target.value)}
                    className="luxury-form-input"
                    placeholder="SW1A 1AA"
                  />
                </div>

                <div className="luxury-form-group">
                  <label className="luxury-form-label">
                    <FiMapPin size={14} />
                    <span>Country</span>
                  </label>
                  <input
                    value={address.country}
                    disabled
                    className="luxury-form-input luxury-form-input-disabled"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="luxury-profile-actions">
            <button
              className="luxury-profile-save-btn"
              onClick={updateProfile}
              disabled={loading}
            >
              <FiSave className="luxury-btn-icon" />
              <span>{loading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}